import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import ExportCsvButton from '../components/ExportCsvButton';
import Pagination from '../components/Pagination';
import '../components/Products.css';
import { closetAPI, exportsAPI, productsAPI } from '../services/api';
import { personName } from '../utils/person';

const PER_PAGE_OPTIONS = [10, 15, 25, 50, 100];

const closetNavState = (influencerId, search = '') => ({
  backTo: `/closet/influencers/${influencerId}/products${search}`,
  influencerId,
  isClosetProduct: true,
});

const getProductDisplayStatus = (product) => {
  const status = (product?.status || 'default').toLowerCase();
  const expiresAt = product?.expires_at ? new Date(product.expires_at) : null;
  const isExpired = !!(
    expiresAt &&
    !Number.isNaN(expiresAt.getTime()) &&
    expiresAt < new Date() &&
    status !== 'sold' &&
    status !== 'rejected'
  );
  return isExpired ? 'expired' : status;
};

const getPrimaryImage = (product) => {
  const imgs = product?.images;
  if (!Array.isArray(imgs) || imgs.length === 0) return null;
  const sorted = [...imgs].sort((a, b) => (a.image_order ?? 0) - (b.image_order ?? 0));
  const primary = sorted.find((i) => i.is_primary) || sorted[0];
  return primary?.image_url || null;
};

const parsePage = (value) => {
  const n = parseInt(value || '1', 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const parsePerPage = (value) => {
  const stored = parseInt(localStorage.getItem('per_page') || '', 10);
  const fallback = PER_PAGE_OPTIONS.includes(stored) ? stored : 15;
  const n = parseInt(value || String(fallback), 10);
  return PER_PAGE_OPTIONS.includes(n) ? n : fallback;
};

const ClosetProductsPage = () => {
  const { influencerId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePage(searchParams.get('page'));
  const perPage = parsePerPage(searchParams.get('per_page'));

  const [influencerProducts, setInfluencerProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [heading, setHeading] = useState('Closet products');
  const [meta, setMeta] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [listUpdating, setListUpdating] = useState(false);

  const pagination = useMemo(
    () => ({
      page,
      per_page: perPage,
      total: Number(total) || 0,
    }),
    [page, perPage, total],
  );

  const showPagination = pagination.total > pagination.per_page;
  const searchSuffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const stateForNav = closetNavState(influencerId, searchSuffix);

  const syncParams = useCallback(
    (nextPage, nextPerPage) => {
      const params = new URLSearchParams();
      if (nextPage > 1) params.set('page', String(nextPage));
      if (nextPerPage !== 15) params.set('per_page', String(nextPerPage));
      setSearchParams(params, { replace: false });
    },
    [setSearchParams],
  );

  const loadInfluencerProducts = useCallback(async () => {
    setListUpdating(true);
    try {
      const response = await closetAPI.getInfluencerProducts(influencerId, {
        page,
        per_page: perPage,
      });
      const rows = response.data.data || [];
      const pg = response.data.pagination || {};
      const nextTotal = Number(pg.total) || 0;
      const nextPerPage = Number(pg.per_page) || perPage;
      const nextPage = Number(pg.page) || page;
      const lastPage = Math.max(1, Math.ceil(nextTotal / nextPerPage) || 1);

      setInfluencerProducts(rows);
      setTotal(nextTotal);

      // Clamp out-of-range page back into the URL (e.g. after deletes).
      if (nextPage > lastPage) {
        syncParams(lastPage, nextPerPage);
      }
    } catch (error) {
      toast.error(
        'Error loading products: ' + (error.response?.data?.message || error.message)
      );
    } finally {
      setListUpdating(false);
      setInitialLoading(false);
    }
  }, [influencerId, page, perPage, syncParams]);

  useEffect(() => {
    void loadInfluencerProducts();
  }, [loadInfluencerProducts]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await closetAPI.getInfluencers({ page: 1, per_page: 100 });
        const rows = res.data.data || [];
        const inf = rows.find((i) => String(i.id) === String(influencerId));
        if (!cancelled && inf) {
          setHeading(`${personName(inf)}'s closet`);
          setMeta(`${inf.closet_products_count ?? 0} listings`);
        }
      } catch {
        /* optional title */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [influencerId]);

  const handlePageChange = (nextPage) => {
    syncParams(nextPage, perPage);
  };

  const handlePerPageChange = (nextPerPage) => {
    localStorage.setItem('per_page', String(nextPerPage));
    syncParams(1, nextPerPage);
  };

  const handleDuplicate = async (productId) => {
    if (!window.confirm('Duplicate this product? A pending copy will be created.')) return;
    try {
      const response = await productsAPI.duplicate(productId);
      const newId = response.data.product?.id;
      toast.success('Product duplicated');
      if (newId) {
        navigate(`/products/${newId}/edit`, { state: stateForNav });
      } else {
        void loadInfluencerProducts();
      }
    } catch (error) {
      toast.error(
        'Error duplicating product: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  const paginationControls = showPagination ? (
    <Pagination
      pagination={pagination}
      onPageChange={handlePageChange}
      onPerPageChange={handlePerPageChange}
    />
  ) : null;

  return (
    <AdminSubpage title={heading} backTo="/closet">
      <div className="products-page">
        <div className="page-toolbar">
          <p className="page-meta">
            {initialLoading
              ? 'Loading…'
              : meta || `${pagination.total} product${pagination.total === 1 ? '' : 's'}`}
            {listUpdating && !initialLoading ? ' · Updating…' : ''}
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <ExportCsvButton
              exportRequest={exportsAPI.products}
              filters={{
                influencer_id: influencerId,
                is_closet_product: true,
              }}
              filename="closet-products.csv"
            />
            <button
              type="button"
              className="btn btn-success"
              onClick={() => navigate('/products/new', { state: stateForNav })}
            >
              + New product
            </button>
          </div>
        </div>

        {showPagination ? (
          <div className="products-pagination-bar products-pagination-bar--top">
            {paginationControls}
          </div>
        ) : null}

        {initialLoading ? (
          <div className="loading">Loading products…</div>
        ) : influencerProducts.length === 0 ? (
          <div className="products-empty">
            No products in this closet yet.
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/products/new', { state: stateForNav })}
              >
                Add first product
              </button>
            </div>
          </div>
        ) : (
          <div className={`products-list${listUpdating ? ' products-list--updating' : ''}`}>
            {influencerProducts.map((product) => {
              const displayStatus = getProductDisplayStatus(product);
              const thumb = getPrimaryImage(product);

              return (
                <article key={product.id} className="product-card">
                  <div
                    className="product-card-thumb"
                    onClick={() => navigate(`/products/${product.id}`, { state: stateForNav })}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      navigate(`/products/${product.id}`, { state: stateForNav })
                    }
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${product.title}`}
                  >
                    {thumb ? (
                      <img src={thumb} alt="" />
                    ) : (
                      <span className="product-card-thumb-placeholder">No photo</span>
                    )}
                  </div>

                  <div
                    className="product-card-main"
                    onClick={() => navigate(`/products/${product.id}`, { state: stateForNav })}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      navigate(`/products/${product.id}`, { state: stateForNav })
                    }
                    role="button"
                    tabIndex={0}
                  >
                    <h3 className="product-card-title">{product.title}</h3>
                    <div className="product-card-meta">
                      <span className="product-card-price">
                        {product.currency || 'AED'} {product.price}
                      </span>
                      <span className={`status-badge status-${displayStatus}`}>
                        {displayStatus}
                      </span>
                      <span className="product-type-pill closet">Closet</span>
                    </div>
                    <div className="product-card-sub">
                      <span>{product.brand || 'No brand'}</span>
                      <span>{product.category?.name || 'Uncategorized'}</span>
                    </div>
                  </div>

                  <div className="product-card-actions">
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() =>
                        navigate(`/products/${product.id}`, { state: stateForNav })
                      }
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() =>
                        navigate(`/products/${product.id}/edit`, { state: stateForNav })
                      }
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => void handleDuplicate(product.id)}
                    >
                      Duplicate
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {showPagination ? (
          <div className="products-pagination-bar products-pagination-bar--bottom">
            {paginationControls}
          </div>
        ) : null}
      </div>
    </AdminSubpage>
  );
};

export default ClosetProductsPage;
